import React, { useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import Button from '@atlaskit/button';
import { IconButton } from '@atlaskit/button/new';
import CheckCircleIcon from '@atlaskit/icon/core/success';
import EditorCloseIcon from '@atlaskit/icon/core/close';
import { Box, Grid, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';

import type { FlagsStateContainer } from '@confluence/flags';
import { withFlags } from '@confluence/flags';
import { LivePagesBetaLozenge } from '@confluence/live-pages-features/entry-points/LivePagesLozenges';
import { LiveEditFeedbackCollector } from '@confluence/live-pages-features/entry-points/LiveEditFeedbackCollector';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { usePageContentId } from '@confluence/page-context';
import { mergeLiveDocI18n } from '@confluence/live-pages-utils/entry-points/mergeLiveDocI18n';

import {
	isBannerDismissed,
	dismissBanner,
	permanentlyDismissBanner,
} from './livePagesBannerDismissal';

const COMMUNITY_LINK_URL =
	'https://community.atlassian.com/forums/Live-Docs-Beta/gh-p/Live-Pages-EAP';
const FIND_OUT_MORE_LINK_URL =
	'https://support.atlassian.com/confluence-cloud/docs/create-and-collaborate-in-real-time-with-live-docs/';

const feedbackBannerStyles = xcss({
	padding: 'space.050',
	borderBottom: `1px solid ${token('color.border')}`,
	backgroundColor: 'elevation.surface.sunken',
	color: 'color.text.subtle',
});

const feedbackBannerLayerStyles = xcss({
	zIndex: 'modal',
});

const feedbackBannerMessageStyles = xcss({
	gridArea: 'message',
});

const closeButtonStyles = xcss({
	display: 'flex',
	justifySelf: 'end',
	gridArea: 'close',
});

const i18nBase = defineMessages({
	bannerMessage: {
		id: 'editor-action-bar.feedback-banner.message',
		defaultMessage:
			'This is a live page! Make edits instantly without having to publish. <link>Share feedback</link>',
		description:
			'This is the message on the feedback banner. It informs the user that the page is live page and their changes will update the page without having to publish. At the end is a link that when clicked opens the feedback form.',
	},
	bannerMessageViewOnly: {
		id: 'editor-action-bar.feedback-banner.message-view-only',
		defaultMessage:
			'This is a live page! Anyone with edit access can make edits in real time without having to publish. <link>Share feedback</link>',
		description:
			'This is the message on the feedback banner when the user is in view only mode. It informs the user that the page is live page and anyone with edit access can make updates without having to publish. At the end is a link that when clicked opens the feedback form.',
	},
	bannerClose: {
		id: 'editor-action-bar.feedback-banner.close',
		defaultMessage: 'Close',
		description: 'clicking this icon closes the feedback banner',
	},
	collectorTitle: {
		id: 'editor-action-bar.feedback-collector.title',
		defaultMessage: 'Give feedback',
		description: 'title for the feedback collector',
	},
	collectorTitleDetails: {
		id: 'editor-action-bar.feedback-collector.title-details',
		defaultMessage:
			'Your thoughts are valuable and help us improve. {lineBreak}{lineBreak} If you’d like to chat with us further and stay informed about this Early Access Program, join our {communityGroupLink}.',
		description:
			"This message is seeking the user's input for improvements and invites them to join a specific group for more information and updates about the Early Access Program.",
	},
	collectorCommunityGroup: {
		id: 'editor-action-bar.feedback-collector.community-group',
		defaultMessage: 'Community group',
		description:
			'Clicking this message takes the user to the community group for the Early Access Program.',
	},
	flagTitle: {
		id: 'editor-action-bar.feedback-flag.title',
		defaultMessage: 'Thank you for your feedback',
		description: 'The title of the flag that appears after the user submits feedback.',
	},
	flagDescription: {
		id: 'editor-action-bar.feedback-flag.description',
		defaultMessage: 'Stay in touch and stay informed about this Early Access Program.',
		description:
			'This is the body of the flag after the user submits feedback. It tells the user that they can stay informed on the Early Access Program with the actions below.',
	},
	flagCommunityGroup: {
		id: 'editor-action-bar.feedback-flag.community-group',
		defaultMessage: 'Join the Community group',
		description:
			'The message tells the user to join the community group for the Early Access Program. Clicking this message takes the user to said community group.',
	},
	flagFindOutMore: {
		id: 'editor-action-bar.feedback-flag.find-out-more',
		defaultMessage: 'Find out more',
		description:
			'The message tells the user to learn more about the live page feature. Clicking this message takes the user to documentation about the live page feature.',
	},
});

const i18nLiveDocs = defineMessages({
	bannerMessage: {
		id: 'editor-action-bar.feedback-banner.message.livedocs',
		defaultMessage:
			'This is a live doc! You can make updates instantly without having to publish. <link>Share feedback</link>',
		description: 'Updated version of editor-action-bar.feedback-banner.message',
	},

	bannerMessageViewOnly: {
		id: 'editor-action-bar.feedback-banner.message-view-only.livedocs',
		defaultMessage:
			'This is a live doc! Anyone with edit access can make updates in real time without having to publish. <link>Share feedback</link>',
		description: 'Updated version of editor-action-bar.feedback-banner.message-view-only',
	},
});

const i18n = mergeLiveDocI18n(i18nBase, i18nLiveDocs);

const CommunityGroupLink = () => (
	<a href={COMMUNITY_LINK_URL} target="_blank">
		<FormattedMessage {...i18n.collectorCommunityGroup} />
	</a>
);

export type LivePagesFeedbackBannerProps = {
	isViewOnly?: boolean;
	flags: FlagsStateContainer;
};

const LivePagesFeedbackBannerComponent = ({ isViewOnly, flags }: LivePagesFeedbackBannerProps) => {
	const { formatMessage } = useIntl();
	const [showBanner, setShowBanner] = useState<boolean>(!isBannerDismissed());
	const [showFeedbackCollector, setShowFeedbackCollector] = useState<boolean>(false);
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const [contentId] = usePageContentId();

	const handleOnClickClose = () => {
		dismissBanner();
		setShowBanner(false);
	};

	const handleOnClickFeedbackButton = () => {
		setShowFeedbackCollector(true);
	};

	const FeedbackCollectorOnSubmit = () => {
		permanentlyDismissBanner();
		setShowFeedbackCollector(false);
		void flags.showCustomFlag({
			title: formatMessage(i18n.flagTitle),
			description: formatMessage(i18n.flagDescription),
			customIcon: <CheckCircleIcon color={token('color.icon.success')} label="" />,
			actions: [
				{
					content: formatMessage(i18n.flagCommunityGroup),
					href: COMMUNITY_LINK_URL,
					target: '_blank',
				},
				{
					content: formatMessage(i18n.flagFindOutMore),
					href: FIND_OUT_MORE_LINK_URL,
					target: '_blank',
				},
			],
		});
	};

	return showBanner ? (
		<div data-vc="live-pages-feedback-banner" {...ssrPlaceholderIdProp}>
			<Grid
				templateAreas={['empty message close']}
				templateColumns="1fr auto 1fr"
				xcss={[feedbackBannerStyles, feedbackBannerLayerStyles]}
				testId="live-pages-feedback-banner"
			>
				<Inline
					space="space.100"
					alignInline="center"
					alignBlock="center"
					xcss={feedbackBannerMessageStyles}
				>
					<LivePagesBetaLozenge />
					<FormattedMessage
						{...(isViewOnly ? i18n.bannerMessageViewOnly : i18n.bannerMessage)}
						values={{
							link: (chunks: React.ReactNode[]) => (
								<Button appearance="link" spacing="none" onClick={handleOnClickFeedbackButton}>
									{chunks}
								</Button>
							),
						}}
					/>
				</Inline>
				<Box xcss={closeButtonStyles}>
					<IconButton
						appearance="subtle"
						spacing="compact"
						icon={EditorCloseIcon}
						label={formatMessage(i18n.bannerClose)}
						onClick={handleOnClickClose}
					/>
				</Box>
			</Grid>

			{showFeedbackCollector && (
				<LiveEditFeedbackCollector
					feedbackTitle={formatMessage(i18n.collectorTitle)}
					feedbackTitleDetails={
						formatMessage(i18n.collectorTitleDetails, {
							lineBreak: <br />,
							communityGroupLink: <CommunityGroupLink />,
						}) as React.ReactChild
					}
					onClose={() => setShowFeedbackCollector(false)}
					onSubmit={FeedbackCollectorOnSubmit}
					source="live-pages-feedback-banner"
					contentId={contentId}
				/>
			)}
		</div>
	) : null;
};

export const LivePagesFeedbackBanner = withFlags(LivePagesFeedbackBannerComponent);
